import React from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { Routes, Route, Navigate } from 'react-router'
import { LoadingPlaceholder } from '@mobi/component-library/Common/LoadingPlaceholder'
import { GridCell } from '@mobi/component-library/Common/Grid'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { MainContainer } from '@core/Components/Containers'
import { ApiServiceError, fetchFromApi } from '@classic/Foundation/Services/ApiService'
import { NoticeBox } from '@core/Components/NoticeBox'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { SpecialTile, SpecialTileProp } from './SpecialTile'
import { SpecialsTermsPage } from './SpecialsTermsPage'

export const SpecialsPage = (): JSX.Element | null => {
  return (
    <MainContainer>
      <PageHeader title='Specials' />
      <Routes>
        <Route path='/' element={<SpecialsTiles />} />
        <Route path='/terms' element={<SpecialsTermsPage />} />
        <Route path='*' element={<Navigate to='/specials' />} />
      </Routes>
    </MainContainer>
  )
}

const SpecialsTiles = () => {
  const { isLoading, isError, data } = useQuery<SpecialTileProp[], ApiServiceError>({
    queryKey: [queryKeys.specials],
    queryFn: getSpecials,
    retry: false,
  })

  if (isLoading) {
    return (
      <GridCell padding='0 1rem'>
        <LoadingPlaceholder width='100%' height='7rem' />
        <LoadingPlaceholder width='100%' height='7rem' />
        <LoadingPlaceholder width='100%' height='7rem' />
      </GridCell>
    )
  }

  if (isError) {
    return (
      <SpecialsErrorNoticeBoxStyled>
        <NoticeBox
          title='Unable to display specials at this time. Please try again later.'
          noticeBoxType='Info'
          hasBorder={true}
        />
      </SpecialsErrorNoticeBoxStyled>
    )
  }

  return (
    <>
      <SpecialTile special={superPick as SpecialTileProp} />
      {data?.map((special, index) => {
        return <SpecialTile key={index} special={special} />
      })}
    </>
  )
}

const getSpecials = async (): Promise<SpecialTileProp[]> => {
  const specials = await fetchFromApi('/api/specials/offers')
  return await specials.json()
}

export const SpecialsErrorNoticeBoxStyled = styled.div({
  padding: '1rem 1rem 0rem',
})

const superPick = {
  title: 'SuperPick',
  description:
    'The power to choose your special. Simply pick your race, pick your runner and pick your special.',
  iconCode: 'specials',
}
